import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { useOnClickOutside } from '@hooks/useOnClickOutside'
import { noop } from '@utils/noop'
import { FC, ReactNode, useRef, useState } from 'react'
import { twMerge } from '@lib/tailwind-merge'
import { Button, Props as ButtonProps } from './Button'
import { DropdownMenu, DropdownMenuProps } from 'components/menus'

interface Props {
  beforeMenuItems?: ReactNode
  button?: (args: {
    disabled: boolean
    menuHidden: boolean
    toggleMenuHidden: () => void
  }) => ReactNode
  buttonVariant?: ButtonProps['variant']
  children?: ReactNode
  className?: string | ((options: { hidden: boolean }) => string)
  closeOnMouseLeave?: boolean
  closeOnItemClick?: boolean
  disabled?: boolean
  dropdownMenuHeader?: string
  menuItemTag?: DropdownMenuProps['itemTag']
  menuItems: DropdownMenuProps['items']
  onMenuHiddenChange?: (hidden: boolean) => void
  testid?: string
  title?: string
}

export const MenuButton: FC<Props> = ({
  beforeMenuItems,
  button,
  buttonVariant,
  children,
  className,
  closeOnMouseLeave = false,
  closeOnItemClick = true,
  disabled = false,
  dropdownMenuHeader,
  menuItemTag,
  menuItems,
  onMenuHiddenChange = noop,
  testid = 'menu-button-wrapper',
  title,
}) => {
  const [menuHidden, setMenuHidden] = useState(true)

  const ref = useRef(null)

  const closeMenu = () => {
    setMenuHidden(true)
    onMenuHiddenChange(true)
  }

  const toggleMenu = () => {
    setMenuHidden((hidden) => {
      onMenuHiddenChange(!hidden)
      return !hidden
    })
  }

  useOnClickOutside(ref, closeMenu)

  const renderButton = () => {
    if (!button)
      return (
        <Button
          data-tooltip-id={title}
          disabled={disabled}
          onClick={toggleMenu}
          icon={menuHidden ? faChevronDown : faChevronUp}
          title={title}
          variant={buttonVariant}
        >
          {children}
        </Button>
      )

    return button({
      toggleMenuHidden: toggleMenu,
      disabled,
      menuHidden,
    })
  }

  return (
    <div
      className={twMerge(
        'relative flex items-center text-sm justify-items-end w-max',
        typeof className === 'function'
          ? className({ hidden: menuHidden })
          : className,
      )}
      ref={ref}
      data-testid={testid}
    >
      {renderButton()}
      <DropdownMenu
        header={dropdownMenuHeader}
        hidden={menuHidden}
        beforeItems={beforeMenuItems}
        items={menuItems}
        onItemClick={() => closeOnItemClick && closeMenu()}
        onMouseLeave={() => closeOnMouseLeave && closeMenu()}
        className="top-[120%] z-30"
        itemTag={menuItemTag}
      />
    </div>
  )
}
